/**
 * keystore.ts — the ONLY module that persists E2EE private keys.
 *
 * Lives on the CLI, under `~/.aethereum/keys/<roomId>.json` (mode 0o600), SEPARATE
 * from `config.json` (which holds the bearer token). This separation is deliberate
 * (NG-K1/K3): the room bearer token and the identity/wrap private keys have different
 * blast radii and different lifecycles, so they never share a file. This module must
 * NEVER be imported by `apps/web` — a CI grep asserts the sentinel below is absent
 * from `apps/web/.next`, proving no private-key path leaked into the server bundle.
 *
 * On-disk shape, per room:
 *   {
 *     version: 1,
 *     roomId, member, alg, checksum,
 *     identity: { pub: b64, priv: b64 },   // Ed25519 (the long-lived trust anchor)
 *     wrap:     { pub: b64, priv: b64 },    // X25519  (room-key wrapping)
 *     roomKeys: { "<epoch>": b64 },         // unwrapped room-key cache, indexed BY EPOCH
 *     activeEpoch: number
 *   }
 *
 * Design invariants:
 *  - provisionKeystore is IDEMPOTENT: if a keystore already exists it is returned
 *    unchanged. Re-provisioning would mint a NEW identity, which is a silent key
 *    change indistinguishable from an active-server substitution (NG-K1). Never do it.
 *  - loadKeystore HARD-FAILS on corrupt/partial/invalid data and NEVER auto-regenerates
 *    (NG-K4): a silent regenerate would mint new keys (looks like an attack) and lose
 *    access to every past artifact. A loud error tells the human to restore from backup.
 *  - roomKeys retains EVERY past epoch key (NG-A7): rotation bumps the active epoch but
 *    old keys are kept so old artifacts still decrypt.
 *  - Every write is ATOMIC: temp file in the same dir -> fsync -> rename over target.
 *    Never truncate-in-place, so a crash/disk-full mid-write cannot leave a partial
 *    keystore that a naive reader might "repair" by regenerating (NG-K4).
 */

import {
  mkdirSync,
  existsSync,
  readFileSync,
  openSync,
  writeSync,
  fsyncSync,
  closeSync,
  renameSync,
  unlinkSync,
} from "node:fs";
import { join, dirname } from "node:path";
import { homedir } from "node:os";
import { e2ee } from "@br9704/aethereum-shared";

/**
 * Greppable sentinel. The CI bundle check asserts this string never appears in
 * `apps/web/.next` — its presence would mean a private-key module leaked into the
 * server build. Keep it a plain string literal so a raw grep finds it.
 */
export const AETHEREUM_KEYSTORE_PRIVATE = "aethereum-e2ee-keystore/v1";

export const KEYSTORE_VERSION = 1 as const;

export interface KeystoreData {
  version: typeof KEYSTORE_VERSION;
  roomId: string;
  member: string;
  alg: string;
  /** Hex sha256 over the canonical key material (integrity guard for NG-K4). */
  checksum: string;
  identity: { pub: string; priv: string };
  wrap: { pub: string; priv: string };
  /** Unwrapped room-key cache, indexed by epoch (string keys), base64 values. */
  roomKeys: Record<string, string>;
  activeEpoch: number;
}

function b64(bytes: Uint8Array): string {
  return Buffer.from(bytes).toString("base64");
}

function unb64(s: string): Uint8Array {
  return new Uint8Array(Buffer.from(s, "base64"));
}

/** Directory that holds the per-room key files: `~/.aethereum/keys/`. */
export function keysDir(home: string = homedir()): string {
  return join(home, ".aethereum", "keys");
}

/** Absolute path of a room's keystore file: `~/.aethereum/keys/<roomId>.json`. */
export function keystorePath(roomId: string, home: string = homedir()): string {
  return join(keysDir(home), `${roomId}.json`);
}

export function hasKeystore(roomId: string, home: string = homedir()): boolean {
  return existsSync(keystorePath(roomId, home));
}

/**
 * Deterministic serialization of the key material used for the integrity checksum.
 * Excludes the `checksum` field itself. roomKeys epochs are sorted numerically so the
 * digest is stable regardless of insertion order.
 */
function canonicalForChecksum(d: Omit<KeystoreData, "checksum">): string {
  const epochs = Object.keys(d.roomKeys)
    .map((e) => [Number(e), d.roomKeys[e]] as const)
    .sort((a, b) => a[0] - b[0])
    .map(([e, v]) => `${e}=${v}`)
    .join(",");
  return [
    `v${d.version}`,
    `room:${d.roomId}`,
    `member:${d.member}`,
    `alg:${d.alg}`,
    `id.pub:${d.identity.pub}`,
    `id.priv:${d.identity.priv}`,
    `wrap.pub:${d.wrap.pub}`,
    `wrap.priv:${d.wrap.priv}`,
    `epoch:${d.activeEpoch}`,
    `roomKeys:${epochs}`,
  ].join("\n");
}

function computeChecksum(d: Omit<KeystoreData, "checksum">): string {
  return Buffer.from(e2ee.sha256(new TextEncoder().encode(canonicalForChecksum(d)))).toString("hex");
}

/**
 * Atomically write the keystore file: fresh temp file in the same directory (mode
 * 0o600), fsync, then rename over the target. A crash between steps leaves either the
 * old file intact or a stray temp file, never a truncated target (NG-K4).
 */
function atomicWrite(path: string, data: KeystoreData): void {
  const dir = dirname(path);
  mkdirSync(dir, { recursive: true, mode: 0o700 });
  const tmp = join(dir, `.${data.roomId}.${process.pid}.${Date.now()}.tmp`);
  const body = JSON.stringify(data, null, 2) + "\n";
  const fd = openSync(tmp, "wx", 0o600);
  try {
    writeSync(fd, body);
    fsyncSync(fd);
  } finally {
    closeSync(fd);
  }
  try {
    renameSync(tmp, path);
  } catch (err) {
    try {
      unlinkSync(tmp);
    } catch {
      /* best effort cleanup */
    }
    throw err;
  }
}

function withChecksum(base: Omit<KeystoreData, "checksum">): KeystoreData {
  return { ...base, checksum: computeChecksum(base) };
}

/**
 * Generate an identity + wrap keypair, persist atomically, and return the keystore.
 * IDEMPOTENT: if a keystore already exists for this room it is loaded and returned
 * WITHOUT overwriting (re-provisioning would silently mint a new identity — NG-K1).
 */
export function provisionKeystore(
  roomId: string,
  member: string,
  home: string = homedir(),
): KeystoreData {
  if (hasKeystore(roomId, home)) {
    // Do NOT overwrite. Return the existing keystore so the identity is stable.
    return loadKeystore(roomId, home);
  }
  const kp = e2ee.generateMemberKeypair();
  const data = withChecksum({
    version: KEYSTORE_VERSION,
    roomId,
    member,
    alg: e2ee.E2EE_ALG,
    identity: { pub: b64(kp.identityPub), priv: b64(kp.identityPriv) },
    wrap: { pub: b64(kp.wrapPub), priv: b64(kp.wrapPriv) },
    roomKeys: {},
    activeEpoch: 0,
  });
  atomicWrite(keystorePath(roomId, home), data);
  return data;
}

/**
 * Read + validate a keystore. HARD-FAILS loudly on corrupt / partial / invalid data
 * and NEVER auto-regenerates (NG-K4): the caller must restore from backup rather than
 * silently mint new keys and lose access to every past artifact.
 */
export function loadKeystore(roomId: string, home: string = homedir()): KeystoreData {
  const path = keystorePath(roomId, home);
  let raw: string;
  try {
    raw = readFileSync(path, "utf8");
  } catch {
    throw new Error(
      `keystore missing for room ${roomId} (${path}). This is NOT recoverable by regenerating — restore from backup.`,
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(
      `keystore corrupt for room ${roomId} (${path}): not valid JSON — restore from backup. Regenerating would mint a new identity and lose access to every past artifact.`,
    );
  }

  const d = parsed as Partial<KeystoreData>;
  const shapeOk =
    d != null &&
    typeof d === "object" &&
    d.version === KEYSTORE_VERSION &&
    typeof d.roomId === "string" &&
    typeof d.member === "string" &&
    typeof d.alg === "string" &&
    typeof d.checksum === "string" &&
    d.identity != null &&
    typeof d.identity.pub === "string" &&
    typeof d.identity.priv === "string" &&
    d.wrap != null &&
    typeof d.wrap.pub === "string" &&
    typeof d.wrap.priv === "string" &&
    d.roomKeys != null &&
    typeof d.roomKeys === "object" &&
    typeof d.activeEpoch === "number" &&
    Number.isInteger(d.activeEpoch);
  if (!shapeOk) {
    throw new Error(
      `keystore corrupt for room ${roomId} (${path}): missing or malformed fields — restore from backup. NEVER auto-regenerating (that would silently change your identity key).`,
    );
  }

  if (d.roomId !== roomId) {
    throw new Error(
      `keystore corrupt for room ${roomId} (${path}): file is for room ${d.roomId} — restore from backup.`,
    );
  }

  const full = d as KeystoreData;
  const { checksum, ...rest } = full;
  const expected = computeChecksum(rest);
  if (checksum !== expected) {
    throw new Error(
      `keystore corrupt for room ${roomId} (${path}): checksum mismatch (partial write or tampering) — restore from backup. NEVER auto-regenerating.`,
    );
  }

  return full;
}

/** In-memory helper: return the parsed private keypair material as bytes. */
export function keypairBytes(data: KeystoreData): {
  identityPub: Uint8Array;
  identityPriv: Uint8Array;
  wrapPub: Uint8Array;
  wrapPriv: Uint8Array;
} {
  return {
    identityPub: unb64(data.identity.pub),
    identityPriv: unb64(data.identity.priv),
    wrapPub: unb64(data.wrap.pub),
    wrapPriv: unb64(data.wrap.priv),
  };
}

/**
 * Cache an unwrapped room key under its epoch. Retains every past epoch key (NG-A7)
 * so old artifacts stay decryptable after a rotation. Atomic write.
 */
export function saveRoomKey(
  roomId: string,
  epoch: number,
  key: Uint8Array,
  home: string = homedir(),
): KeystoreData {
  const data = loadKeystore(roomId, home);
  const roomKeys = { ...data.roomKeys, [String(epoch)]: b64(key) };
  const next = withChecksum({ ...stripChecksum(data), roomKeys });
  atomicWrite(keystorePath(roomId, home), next);
  return next;
}

/** Return the cached room key for an epoch, or null if this member never held it. */
export function getRoomKey(
  roomId: string,
  epoch: number,
  home: string = homedir(),
): Uint8Array | null {
  const data = loadKeystore(roomId, home);
  const v = data.roomKeys[String(epoch)];
  return v ? unb64(v) : null;
}

/** Set the active epoch (rotation). Past room keys are retained (NG-A7). Atomic write. */
export function setActiveEpoch(
  roomId: string,
  epoch: number,
  home: string = homedir(),
): KeystoreData {
  const data = loadKeystore(roomId, home);
  const next = withChecksum({ ...stripChecksum(data), activeEpoch: epoch });
  atomicWrite(keystorePath(roomId, home), next);
  return next;
}

function stripChecksum(d: KeystoreData): Omit<KeystoreData, "checksum"> {
  const { checksum: _checksum, ...rest } = d;
  return rest;
}
